import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { retirosApi, remitosApi, notasEnvioApi } from '../api/remitos'
import { fmtFecha, fmtFechaHora, fmtHoraDe } from '../utils/fecha'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useStoredState } from '../hooks/useStoredState'
import { colors, font, text, radius, sx } from '../design/tokens'
import Boton from '../components/Boton'

/**
 * Retiros por sucursal — la cola de lo que el cliente pasa a buscar
 * (Martin, 28 ago 2026; maqueta aprobada). Un retiro no es un viaje: no tiene
 * salida, fletero ni zona. Cada sucursal ve lo suyo; la espera desde el aviso
 * es el dato principal; una sola acción por fila según la etapa.
 *
 * Solo remitos CONFIRMADOS (regla de Martin: nada sin confirmar en distribución).
 */

const ORIGEN_LABEL = { magento: 'Tienda', storefront: 'Tienda', wine_club: 'Wine Club' }
const origenLabel = (o) => ORIGEN_LABEL[o] || 'LGR'

const HOY_MS = () => Date.now()
const diasDesde = (iso) => Math.floor((HOY_MS() - new Date(iso).getTime()) / 86400000)
const fmtDias = (n) => n <= 0 ? 'hoy' : n === 1 ? '1 día' : `${n} días`

// Etapa de la fila: sin avisar → avisado → demorado (avisado hace >= N días)
function etapaDe(r, demora) {
  if (!r.disponible_retiro_at) return 'sinaviso'
  return diasDesde(r.disponible_retiro_at) >= demora ? 'demorado' : 'avisado'
}
const ORDEN_ETAPA = { sinaviso: 0, demorado: 1, avisado: 2 }

// La cola mezcla remitos de retiro y PARTES de envíos parciales que se retiran
// (notas de envío, Martin 2 nov 2026). Mismas acciones, otra API y otra ruta.
const claveDe = (r) => r.clave || `r${r.id}`
const apiDe = (r) => (r.tipo === 'nota_envio' ? notasEnvioApi : remitosApi)
const rutaDe = (r) => (r.tipo === 'nota_envio' ? `/notas-envio/${r.id}` : `/remitos/${r.id}`)

export default function RetirosPage() {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [sucursal, setSucursal] = useStoredState('lgr.retiros.sucursal', 'todas')
  const [filtro, setFiltro] = useState(null)       // etapa que filtra la tabla (tile), null = todas
  const [busqueda, setBusqueda] = useState('')
  const [confirmando, setConfirmando] = useState(null) // id del remito con la confirmación abierta
  const [quien, setQuien] = useState('')
  const [trabajando, setTrabajando] = useState(null)   // id del remito con una acción en curso
  const [aviso, setAviso] = useState('')               // feedback de la última acción (se va solo)
  const [hoyAbierto, setHoyAbierto] = useState(false)  // "Retirados hoy" desplegado

  const cargar = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await retirosApi.listar()
      setData(res.data)
      setError('')
    } catch (e) {
      if (!silent) setError(e.response?.data?.message || 'No se pudo cargar la cola de retiros.')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(() => cargar(true), 30000, confirmando === null)
  useEffect(() => {
    if (!aviso) return undefined
    const t = setTimeout(() => setAviso(''), 4000)
    return () => clearTimeout(t)
  }, [aviso])

  const demora = data?.demora_dias ?? 3
  const sucursales = useMemo(() => data?.sucursales || [], [data])
  const todos = useMemo(() => data?.pendientes || [], [data])
  const retiradosHoyTodos = useMemo(() => data?.retirados_hoy || [], [data])

  const pendientes = useMemo(
    () => todos.filter(r => sucursal === 'todas' || r.sucursal?.id === sucursal),
    [todos, sucursal],
  )
  const retiradosHoy = useMemo(
    () => retiradosHoyTodos.filter(r => sucursal === 'todas' || r.sucursal?.id === sucursal),
    [retiradosHoyTodos, sucursal],
  )
  const conteo = useMemo(() => {
    const c = { sinaviso: 0, avisado: 0, demorado: 0 }
    pendientes.forEach(r => { c[etapaDe(r, demora)]++ })
    return c
  }, [pendientes, demora])

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    return pendientes
      .filter(r => !filtro || etapaDe(r, demora) === filtro)
      .filter(r => !q || (r.cliente?.nombre || '').toLowerCase().includes(q) || (r.numero_presea || '').includes(q) || (r.nota_venta_numero || '').toLowerCase().includes(q))
      .sort((a, b) => {
        const ea = etapaDe(a, demora), eb = etapaDe(b, demora)
        if (ORDEN_ETAPA[ea] !== ORDEN_ETAPA[eb]) return ORDEN_ETAPA[ea] - ORDEN_ETAPA[eb]
        const da = diasDesde(a.disponible_retiro_at || a.created_at), db = diasDesde(b.disponible_retiro_at || b.created_at)
        return db - da
      })
  }, [pendientes, filtro, busqueda, demora])

  // Mismo cliente con más de un remito en la cola: se marca para que se entreguen juntos
  const porCliente = useMemo(() => {
    const m = {}
    pendientes.forEach(r => { const k = r.cliente?.id || `n${r.id}`; m[k] = (m[k] || 0) + 1 })
    return m
  }, [pendientes])

  const tabs = useMemo(() => {
    const t = [{ id: 'todas', nombre: 'Todas', n: todos.length, demorados: todos.some(r => etapaDe(r, demora) === 'demorado') }]
    sucursales.forEach(s => {
      const l = todos.filter(r => r.sucursal?.id === s.id)
      t.push({ id: s.id, nombre: s.nombre, n: l.length, demorados: l.some(r => etapaDe(r, demora) === 'demorado') })
    })
    return t
  }, [todos, sucursales, demora])
  const sucInfo = sucursales.find(s => s.id === sucursal)

  const correr = async (id, fn, ok) => {
    setTrabajando(id); setAviso('')
    try {
      const res = await fn()
      setAviso(typeof ok === 'function' ? ok(res) : ok)
      await cargar(true)
    } catch (e) {
      setAviso(e.response?.data?.message || 'No se pudo completar la acción.')
    } finally { setTrabajando(null) }
  }

  const avisarListo = (r) => correr(claveDe(r), () => apiDe(r).disponibleRetiro(r.id), (res) => `Marcado como listo — ${res.data?.aviso_detalle || 'aviso enviado'}.`)
  const reavisar = (r) => correr(claveDe(r), () => apiDe(r).reavisarRetiro(r.id), (res) => `Aviso repetido — ${res.data?.aviso_detalle || 'enviado'}.`)
  const confirmarRetiro = (r) => {
    const q = quien.trim()
    setConfirmando(null); setQuien(''); setHoyAbierto(true)
    return correr(claveDe(r), () => apiDe(r).marcarRetirado(r.id, q ? { quien: q } : {}), `Retirado ✓ ${r.numero_presea || ''} — queda en "Retirados hoy" con Deshacer.`)
  }
  const deshacer = (r) => correr(claveDe(r), () => apiDe(r).deshacerRetiro(r.id), `Retiro deshecho — ${r.numero_presea || 'el remito'} vuelve a la cola.`)

  const abrirLista = () => {
    const url = `/logistica/retiros/lista${sucursal !== 'todas' ? `?sucursal=${encodeURIComponent(sucursal)}` : ''}`
    window.open(url, '_blank', 'noopener')
  }

  return (
    <div>
      <div style={st.head}>
        <h1 style={st.h1}>🏬 Retiros por sucursal <span style={st.total}>{todos.length}</span></h1>
        <div style={{ flex: 1 }} />
        <input
          style={st.buscar}
          placeholder="Buscar cliente o remito"
          aria-label="Buscar cliente o remito"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
        <Boton variante="secondary" onClick={abrirLista} title="Imprime lo pendiente de la sucursal con casilleros para separar la mercadería">🖨 Lista para separar</Boton>
      </div>

      {error && <div style={st.error}>{error}</div>}

      <div style={st.card}>
        <div style={st.tabs} role="tablist">
          {tabs.map(t => (
            <button
              key={t.id} type="button" role="tab" aria-selected={sucursal === t.id}
              style={{ ...st.tab, ...(sucursal === t.id ? st.tabOn : {}) }}
              onClick={() => { setSucursal(t.id); setFiltro(null); setConfirmando(null) }}
            >
              {t.nombre} <span style={{ ...st.tabN, ...(sucursal === t.id ? st.tabNOn : {}) }}>{t.n}</span>
              {t.demorados && <span style={st.dot} title="Hay retiros demorados" />}
            </button>
          ))}
        </div>

        <div style={st.sub}>
          {sucInfo
            ? <><b style={{ color: colors.text }}>{sucInfo.nombre}</b> · {sucInfo.direccion}</>
            : <><b style={{ color: colors.text }}>Las {sucursales.length} sucursales</b> · lo ve el depósito y administración</>}
          <span style={{ marginLeft: 'auto' }}>Demorado = avisado hace {demora} días o más</span>
        </div>

        <div style={st.tiles}>
          <Tile k="sinaviso" titulo="Para separar y avisar" n={conteo.sinaviso} sub="sin aviso al cliente" on={filtro === 'sinaviso'} onClick={() => setFiltro(filtro === 'sinaviso' ? null : 'sinaviso')} />
          <Tile k="avisado" titulo="Esperando al cliente" n={conteo.avisado} sub={`avisados hace menos de ${demora} días`} on={filtro === 'avisado'} onClick={() => setFiltro(filtro === 'avisado' ? null : 'avisado')} />
          <Tile k="demorado" titulo="Demorados" n={conteo.demorado} sub={`+${demora} días desde el aviso`} on={filtro === 'demorado'} color={conteo.demorado ? colors.warn : undefined} onClick={() => setFiltro(filtro === 'demorado' ? null : 'demorado')} />
          <Tile k="hoy" titulo="Retirados hoy" n={retiradosHoy.length} sub="se pueden deshacer" color={retiradosHoy.length ? colors.ok : undefined} ultimo />
        </div>

        {aviso && <div style={st.aviso}>{aviso}</div>}

        {loading ? (
          <p style={st.vacio}>Cargando…</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={st.tabla}>
              <thead>
                <tr>
                  <th style={{ ...st.th, width: 130 }}>Remito</th>
                  <th style={st.th}>Cliente</th>
                  <th style={{ ...st.th, width: 110 }}>Emitido</th>
                  <th style={{ ...st.th, width: 140 }}>Estado</th>
                  <th style={{ ...st.th, width: 130 }}>Espera</th>
                  <th style={{ ...st.th, width: 240 }} />
                </tr>
              </thead>
              <tbody>
                {visibles.length === 0 && (
                  <tr><td colSpan={6} style={st.vacio}>
                    {busqueda ? `Nada coincide con "${busqueda}".` : filtro ? 'No hay retiros en esta etapa.' : 'Sin retiros pendientes en esta sucursal.'}
                  </td></tr>
                )}
                {visibles.map(r => {
                  const etapa = etapaDe(r, demora)
                  const dias = diasDesde(r.disponible_retiro_at || r.created_at)
                  const ocupado = trabajando === claveDe(r)
                  const abierto = confirmando === claveDe(r)
                  return [
                    <tr
                      key={claveDe(r)}
                      style={{ ...st.tr, ...(abierto ? { background: colors.bgSoft } : {}) }}
                      title="Ver el remito"
                      onClick={() => navigate(rutaDe(r))}
                      onMouseEnter={e => { if (!abierto) e.currentTarget.style.background = colors.bgSoft }}
                      onMouseLeave={e => { if (!abierto) e.currentTarget.style.background = '' }}
                    >
                      <td style={{ ...st.td, ...sx.mono, color: colors.link, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.numero_presea || r.numero || '—'}</td>
                      <td style={st.td}>
                        <div style={st.cli}>
                          {r.cliente?.nombre || '—'}
                          {(porCliente[r.cliente?.id] || 0) > 1 && <span style={st.mas}>+{porCliente[r.cliente.id] - 1} remito del mismo cliente</span>}
                        </div>
                        <div style={st.subl}>
                          <span style={{ fontWeight: 600, color: colors.textSec }}>{origenLabel(r.origen)}</span>
                          <span>{r.renglones} rengl{r.renglones === 1 ? 'ón' : 'ones'} · {r.unidades} u.</span>
                          {r.parte_de_envio_parcial && <span title="Una parte de un envío parcial: el resto del remito se despacha aparte">📦 parte de envío parcial</span>}
                          {sucursal === 'todas' && r.sucursal && <span>{r.sucursal.nombre}</span>}
                          {r.canal_aviso !== 'ses' && <span title="El operador avisa desde su casilla: no salen mails automáticos">sin aviso automático</span>}
                        </div>
                      </td>
                      <td style={{ ...st.td, ...sx.mono, color: colors.textSec, whiteSpace: 'nowrap' }}>{fmtFecha(r.fecha_emision || r.created_at)}</td>
                      <td style={st.td}><Chip etapa={etapa} /></td>
                      <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                        <div style={{ ...sx.mono, fontWeight: 700, color: etapa === 'demorado' ? colors.warn : colors.text }}>{fmtDias(dias)}</div>
                        <div style={st.desde}>
                          {r.disponible_retiro_at
                            ? <>último aviso {fmtFechaHora(r.ultimo_aviso_at || r.disponible_retiro_at)}{r.reavisos ? ` · ×${r.reavisos + 1}` : ''}</>
                            : 'desde que se emitió'}
                        </div>
                      </td>
                      <td style={{ ...st.td, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        {abierto ? (
                          <span style={st.hecho}>Confirmá abajo ↓</span>
                        ) : etapa === 'sinaviso' ? (
                          <Boton variante="send" sm disabled={ocupado} onClick={() => avisarListo(r)} title="La sucursal ya tiene el remito y la mercadería — se le avisa al cliente por mail que puede pasar a retirar">
                            {ocupado ? '…' : '📣 Avisar listo'}
                          </Boton>
                        ) : (
                          <>
                            {etapa === 'demorado' && (
                              <Boton variante="ghost" sm disabled={ocupado} onClick={() => reavisar(r)} title="Repetir el aviso al cliente por mail" style={{ marginRight: 6 }}>
                                {ocupado ? '…' : '↻ Reavisar'}
                              </Boton>
                            )}
                            <Boton variante="success" sm disabled={ocupado} onClick={() => { setConfirmando(claveDe(r)); setQuien('') }} title="Registrar que el cliente ya pasó a buscar el pedido">
                              ✓ Retirado
                            </Boton>
                          </>
                        )}
                      </td>
                    </tr>,
                    abierto && (
                      <tr key={`c${claveDe(r)}`}>
                        <td colSpan={6} style={st.confirma} onClick={e => e.stopPropagation()}>
                          <div style={st.pop}>
                            <span style={st.preg}>
                              ¿{r.cliente?.nombre || 'El cliente'} ya retiró el remito <span style={sx.mono}>{r.numero_presea || r.numero}</span>?
                              <small style={st.pregSub}>Queda en el historial del remito con tu usuario y la hora.</small>
                            </span>
                            <input
                              autoFocus
                              style={st.quien}
                              placeholder="Quién retiró — nombre o DNI (opcional)"
                              aria-label="Quién retiró"
                              value={quien}
                              onChange={e => setQuien(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') confirmarRetiro(r); if (e.key === 'Escape') { setConfirmando(null); setQuien('') } }}
                            />
                            <Boton variante="success" sm onClick={() => confirmarRetiro(r)}>✓ Sí, retiró</Boton>
                            <Boton variante="ghost" sm onClick={() => { setConfirmando(null); setQuien('') }}>Cancelar</Boton>
                          </div>
                        </td>
                      </tr>
                    ),
                  ]
                })}
              </tbody>
            </table>
          </div>
        )}

        <details style={st.hoy} open={hoyAbierto} onToggle={e => setHoyAbierto(e.currentTarget.open)}>
          <summary style={st.hoySummary}><span style={{ color: colors.textMeta }}>{hoyAbierto ? '▾' : '▸'}</span> Retirados hoy <span style={st.total}>{retiradosHoy.length}</span></summary>
          {retiradosHoy.length === 0 ? (
            <p style={st.vacio}>Todavía no se marcó ningún retiro hoy.</p>
          ) : (
            <table style={st.tabla}>
              <tbody>
                {retiradosHoy.map(r => (
                  <tr key={claveDe(r)} style={{ ...st.tr, background: colors.okSoft }} title="Ver el remito" onClick={() => navigate(rutaDe(r))}>
                    <td style={{ ...st.td, ...sx.mono, color: colors.textSec, fontWeight: 700, width: 130, whiteSpace: 'nowrap' }}>{r.numero_presea || r.numero || '—'}</td>
                    <td style={st.td}>
                      <div style={{ ...st.cli, color: colors.textSub }}>{r.cliente?.nombre || '—'}</div>
                      <div style={st.subl}>
                        {sucursal === 'todas' && r.sucursal && <span>{r.sucursal.nombre}</span>}
                        {r.retiro_quien && <span>retiró: <b style={{ color: colors.textSub }}>{r.retiro_quien}</b></span>}
                      </div>
                    </td>
                    <td style={{ ...st.td, ...sx.mono, color: colors.textSec, width: 110, whiteSpace: 'nowrap' }}>{fmtFecha(r.fecha_emision || r.created_at)}</td>
                    <td style={{ ...st.td, width: 140 }}><span style={{ ...st.chip, background: colors.okBg, color: colors.ok }}>✓ Retirado {fmtHoraDe(r.retirado_at)}</span></td>
                    <td style={{ ...st.td, width: 130 }} />
                    <td style={{ ...st.td, textAlign: 'right', width: 240 }} onClick={e => e.stopPropagation()}>
                      <Boton variante="ghost" sm disabled={trabajando === claveDe(r)} onClick={() => deshacer(r)} title="Se marcó mal o el cliente cambió la compra: el remito vuelve a la cola">↩ Deshacer</Boton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </details>
      </div>
    </div>
  )
}

function Tile({ titulo, n, sub, on, onClick, color, ultimo }) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      style={{ ...st.tile, ...(ultimo ? { borderRight: 'none' } : {}), ...(on ? st.tileOn : {}), cursor: onClick ? 'pointer' : 'default' }}
      aria-pressed={onClick ? !!on : undefined}
    >
      <span style={st.tileK}>{titulo}</span>
      <span style={{ ...st.tileV, color: color || colors.text }}>{n}<small style={st.tileSub}>{sub}</small></span>
    </Tag>
  )
}

function Chip({ etapa }) {
  if (etapa === 'demorado') return <span style={{ ...st.chip, background: colors.warnBg, color: colors.warn }}>Demorado</span>
  if (etapa === 'avisado') return <span style={{ ...st.chip, background: colors.linkBg, color: colors.link }}>📣 Avisado</span>
  return <span style={{ ...st.chip, background: colors.bgSoft, color: colors.textSub, border: `1px solid ${colors.border}` }}>Sin avisar</span>
}

const st = {
  head: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' },
  h1: { margin: 0, fontSize: '1.5rem', fontWeight: 700, color: colors.textStrong, display: 'flex', alignItems: 'center', gap: 10 },
  total: { display: 'inline-flex', minWidth: 22, height: 22, padding: '0 7px', alignItems: 'center', justifyContent: 'center', borderRadius: radius.pill, background: colors.bgSoft, border: `1px solid ${colors.border}`, ...sx.mono, fontSize: text.sec, fontWeight: 700, color: colors.textSub },
  buscar: { height: 34, width: 240, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '0 10px', fontSize: text.sec, fontFamily: 'inherit', color: colors.text, background: colors.bg },
  error: { background: colors.errBg, color: colors.err, borderRadius: radius.md, padding: '8px 12px', marginBottom: 12, fontSize: text.body },
  aviso: { background: colors.infoBg, color: colors.info, padding: '8px 20px', fontSize: text.sec, borderBottom: `1px solid ${colors.borderSoft}` },
  card: { background: colors.bg, borderRadius: 12, boxShadow: '0 1px 4px rgba(15,23,42,.07)', overflow: 'hidden' },
  tabs: { display: 'flex', gap: 4, padding: '10px 20px 0', borderBottom: `1px solid ${colors.borderSoft}`, overflowX: 'auto' },
  tab: { appearance: 'none', border: 'none', background: 'none', fontFamily: 'inherit', fontSize: text.body, fontWeight: 600, color: colors.textSec, padding: '8px 12px 10px', borderBottom: '2px solid transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: -1, whiteSpace: 'nowrap' },
  tabOn: { color: colors.text, borderBottomColor: colors.text },
  tabN: { ...sx.mono, fontSize: text.meta, fontWeight: 700, color: colors.textSub, background: colors.bgSoft, border: `1px solid ${colors.border}`, borderRadius: radius.pill, padding: '1px 7px' },
  tabNOn: { background: colors.text, borderColor: colors.text, color: colors.bg },
  dot: { width: 7, height: 7, borderRadius: '50%', background: colors.warnStrong, display: 'inline-block' },
  sub: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', fontSize: text.sec, color: colors.textSec, borderBottom: `1px solid ${colors.borderSoft}`, flexWrap: 'wrap' },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', borderBottom: `1px solid ${colors.borderSoft}` },
  tile: { padding: '14px 20px', borderRight: `1px solid ${colors.borderSoft}`, borderTop: 'none', borderBottom: 'none', borderLeft: 'none', background: colors.bg, textAlign: 'left', fontFamily: 'inherit', display: 'block', width: '100%' },
  tileOn: { background: colors.bgSoft, boxShadow: `inset 0 -2px 0 ${colors.text}` },
  tileK: { display: 'block', fontSize: text.meta, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textSec, marginBottom: 4 },
  tileV: { ...sx.mono, fontSize: 24, fontWeight: 700, lineHeight: 1, display: 'flex', alignItems: 'baseline', gap: 8 },
  tileSub: { fontFamily: font.sans, fontSize: text.meta, fontWeight: 500, color: colors.textMeta },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { ...sx.th, padding: '9px 20px' },
  tr: { cursor: 'pointer' },
  td: { ...sx.td, padding: '8px 20px', borderBottom: `1px solid ${colors.borderSoft}`, height: 52, lineHeight: 1.3 },
  cli: { fontWeight: 500, color: colors.text, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  mas: { fontSize: text.meta, fontWeight: 600, color: colors.info, background: colors.infoBg, borderRadius: radius.xs, padding: '1px 6px', whiteSpace: 'nowrap' },
  subl: { fontSize: text.sec, color: colors.textMeta, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' },
  desde: { fontSize: text.meta, color: colors.textMeta },
  chip: { ...sx.badge, display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' },
  hecho: { fontSize: text.sec, color: colors.textSec, fontWeight: 600 },
  confirma: { background: colors.bgSoft, padding: '10px 20px', borderBottom: `1px solid ${colors.border}` },
  pop: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' },
  preg: { fontSize: text.body, color: colors.text, fontWeight: 600, marginRight: 'auto' },
  pregSub: { display: 'block', fontWeight: 400, color: colors.textSec, fontSize: text.sec },
  quien: { height: 30, width: 260, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '0 10px', fontFamily: 'inherit', fontSize: text.sec, textTransform: 'none' },
  hoy: { borderTop: `1px solid ${colors.border}` },
  hoySummary: { cursor: 'pointer', padding: '12px 20px', fontSize: text.sec, fontWeight: 600, color: colors.textSub, display: 'flex', alignItems: 'center', gap: 8, listStyle: 'none' },
  vacio: { padding: '28px 20px', color: colors.textMeta, textAlign: 'center', fontSize: text.body, margin: 0 },
}
